import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Paper,
  Typography,
  Grid,
  Alert,
  IconButton,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  SelectChangeEvent,
} from '@mui/material';
import { Delete } from '@mui/icons-material';
import axios from 'axios';
import { DATA_API_URL, CAM_API_URL, CLIENT_ID, TOKEN } from '../config';
import { getConfig } from '../services/configService';

interface CapturedImage {
  dataUrl: string;
  filename: string;
}

const CameraCapture: React.FC = () => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [capturedImages, setCapturedImages] = useState<CapturedImage[]>([]);
  const [truckNumber, setTruckNumber] = useState('');
  const [height, setHeight] = useState('');
  const [length, setLength] = useState('');
  const [breadth, setBreadth] = useState('');
  const [lightCondition, setLightCondition] = useState('');
  const [climate, setClimate] = useState('');
  const [truckType, setTruckType] = useState('');
  const [entryType, setEntryType] = useState('');
  const [bedColor, setBedColor] = useState('');
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [availableCameras, setAvailableCameras] = useState<MediaDeviceInfo[]>([]);
  const [selectedCamera, setSelectedCamera] = useState<string>('');
  const [config, setConfig] = useState<{
    lightConditions: string[];
    climateConditions: string[];
    truckTypes: string[];
    entryTypes: string[];
    bedColors: string[];
  }>({
    lightConditions: [],
    climateConditions: [],
    truckTypes: [],
    entryTypes: [],
    bedColors: []
  });
  const videoFeedRef = useRef<HTMLImageElement | null>(null);

  useEffect(() => {
    // Get list of available cameras when component mounts
    const getCameras = async () => {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoDevices = devices.filter(device => device.kind === 'videoinput');
        setAvailableCameras(videoDevices);
        if (videoDevices.length > 0) {
          setSelectedCamera(videoDevices[0].deviceId);
        }
      } catch (err) {
        console.error('Error getting cameras:', err);
        setError('Failed to access camera devices');
      }
    };
    getCameras();
  }, []);

  useEffect(() => {
    const loadConfig = async () => {
      const configData = await getConfig();
      setConfig(configData);
    };
    loadConfig();
  }, []);

  const startCamera = async () => {
    try {
      // Reset any previous error
      setError(null);
      
      // Add video feed element if it doesn't exist
      if (!videoFeedRef.current) {
        const videoFeedBox = document.getElementById('video-feed-box');
        if (videoFeedBox) {
          const img = document.createElement('img');
          img.src = `${CAM_API_URL}/cam-server/v1/video-feed`;
          img.alt = "Camera Feed";
          img.style.width = '100%';
          img.style.height = 'auto';
          img.style.display = 'block';
          videoFeedBox.appendChild(img);
          videoFeedRef.current = img;
        }
      }
      setIsCameraActive(true);
    } catch (err) {
      console.error('Error accessing camera:', err);
      setError('Failed to access camera. Please ensure you have granted camera permissions.');
      setIsCameraActive(false);
    }
  };

  const stopCamera = async () => {
    try {
      // First call the stop video feed endpoint
      const response = await fetch(`${CAM_API_URL}/cam-server/v1/video-feed/stop`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to stop video feed');
      }

      const data = await response.json();
      if (data.status === 'success') {
        // Then remove the video feed element
        if (videoFeedRef.current) {
          videoFeedRef.current.remove();
          videoFeedRef.current = null;
        }
        setIsCameraActive(false);
      } else {
        throw new Error(data.message || 'Failed to stop video feed');
      }
    } catch (err) {
      console.error('Error stopping camera:', err);
      setError('Failed to stop camera. Please try again.');
    } finally {
      // Always reset the camera state
      setIsCameraActive(false);
    }
  };

  const captureImage = async () => {
    if (!isCameraActive) {
      setError('Please start the camera first');
      return;
    }

    if (!truckNumber.trim()) {
      setError('Please enter a truck number before capturing');
      return;
    }

    try {
      const response = await fetch(`${CAM_API_URL}/cam-server/v1/capture`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Token': TOKEN,
        },
        body: JSON.stringify({ truck_number: truckNumber }),
      });

      const data = await response.json();
      console.log('Capture response:', data); // Debug log

      if (response.ok && data.preview_url && data.filename) {
        // Add the captured image to the list with both preview URL and filename
        const newImage = {
          dataUrl: data.preview_url.replace(/^https?:\/\/[^/]+/, CAM_API_URL),
          filename: data.filename
        };
        console.log('Adding new image:', newImage); // Debug log
        setCapturedImages(prev => [...prev, newImage]);
        setSuccess('Image captured successfully!');
        setError(null);
      } else {
        throw new Error(data.message || 'Failed to capture image');
      }
    } catch (err) {
      console.error('Error capturing image:', err);
      setError(err instanceof Error ? err.message : 'Failed to capture image. Please try again.');
      setSuccess(null);
    }
  };

  // const dataURLtoBlob = (dataUrl: string): Blob => {
  //   const arr = dataUrl.split(',');
  //   const mime = arr[0].match(/:(.*?);/)?.[1];
  //   const bstr = atob(arr[1]);
  //   let n = bstr.length;
  //   const u8arr = new Uint8Array(n);
  //   while (n--) {
  //     u8arr[n] = bstr.charCodeAt(n);
  //   }
  //   return new Blob([u8arr], { type: mime });
  // };

  const removeImage = (index: number) => {
    setCapturedImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!truckNumber || !height || !length || !breadth || !lightCondition || !climate || !truckType || !entryType || !bedColor) {
      setError('Please fill in all fields');
      return;
    }

    if (capturedImages.length === 0) {
      setError('Please capture at least one image');
      return;
    }

    const requestData = {
      truck_number: truckNumber,
      height: height,
      length: length,
      breadth: breadth,
      light_condition: lightCondition,
      climate: climate,
      truck_type: truckType,
      entry_type: entryType,
      bed_color: bedColor,
      images: capturedImages.map(img => img.filename)
    };

    try {
      const response = await fetch(`${DATA_API_URL}/data-server/v1/truck-entry`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Client-Id': CLIENT_ID,
          'Accept': 'application/json',
        },
        body: JSON.stringify(requestData),
      });

      if (!response.ok) {
        throw new Error('Failed to save images');
      }

      const data = await response.json();
      if (response.ok) {
        setSuccess('Images saved successfully!');
        setCapturedImages([]); // Clear captured images after successful submission
        setError(null);
      } else {
        throw new Error(data.message || 'Failed to save images');
      }
    } catch (err) {
      setError('Failed to save images. Please try again.');
    }
  };

  const fetchTruckMeasurements = async (truckNumber: string) => {
    if (!truckNumber) return;
    
    try {
      const response = await axios.get(`${DATA_API_URL}/data-server/v1/truck-entry/${truckNumber}`, {
        headers: {
          'X-Client-Id': CLIENT_ID
        }
      });
      
      const measurements = response.data;
      setHeight(measurements.height.toString());
      setLength(measurements.length.toString());
      setBreadth(measurements.breadth.toString());
      setBedColor(measurements.bed_color);
      setTruckType(measurements.truck_type);
    } catch (error) {
      // If truck not found or other error, just log it and continue
      console.log('No previous measurements found for this truck');
    }
  };

  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      if (videoFeedRef.current) {
        videoFeedRef.current.remove();
        videoFeedRef.current = null;
      }
      // Call stop camera to ensure cleanup
      stopCamera();
    };
  }, []);

  return (
    <Paper elevation={3} sx={{ p: 3, maxWidth: 800, mx: 'auto', mt: 4 }}>
      <Typography variant="h5" gutterBottom>
        Capture Images
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Truck Number"
              value={truckNumber}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTruckNumber(e.target.value.toUpperCase())}
              onBlur={(e: React.FocusEvent<HTMLInputElement>) => fetchTruckMeasurements(e.target.value)}
              inputProps={{ style: { textTransform: 'uppercase' } }}
              required
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Height (cm)"
              type="number"
              value={height}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setHeight(e.target.value)}
              required
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Length (cm)"
              type="number"
              value={length}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLength(e.target.value)}
              required
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Breadth (cm)"
              type="number"
              value={breadth}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setBreadth(e.target.value)}
              required
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth required>
              <InputLabel>Light Condition</InputLabel>
              <Select
                value={lightCondition}
                onChange={(e: SelectChangeEvent) => setLightCondition(e.target.value)}
                label="Light Condition"
              >
                {config.lightConditions.map((condition) => (
                  <MenuItem key={condition} value={condition}>
                    {condition}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth required>
              <InputLabel>Climate</InputLabel>
              <Select
                value={climate}
                onChange={(e: SelectChangeEvent) => setClimate(e.target.value)}
                label="Climate"
              >
                {config.climateConditions.map((condition) => (
                  <MenuItem key={condition} value={condition}>
                    {condition}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth required>
              <InputLabel>Truck Type</InputLabel>
              <Select
                value={truckType}
                onChange={(e: SelectChangeEvent) => setTruckType(e.target.value)}
                label="Truck Type"
              >
                {config.truckTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth required>
              <InputLabel>Entry Type</InputLabel>
              <Select
                value={entryType}
                onChange={(e: SelectChangeEvent) => setEntryType(e.target.value)}
                label="Entry Type"
              >
                {config.entryTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth required>
              <InputLabel>Bed Color</InputLabel>
              <Select
                value={bedColor}
                onChange={(e: SelectChangeEvent) => setBedColor(e.target.value)}
                label="Bed Color"
              >
                {config.bedColors.map((color) => (
                  <MenuItem key={color} value={color}>
                    {color}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            {error && (
              <Typography color="error" gutterBottom>
                {error}
              </Typography>
            )}
            <Button
              variant="contained"
              color="primary"
              onClick={startCamera}
              disabled={isCameraActive}
              sx={{ mr: 2 }}
            >
              Start Camera
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={captureImage}
              disabled={!isCameraActive}
              sx={{ mr: 2 }}
            >
              Capture Image
            </Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={stopCamera}
              disabled={!isCameraActive}
            >
              Stop Camera
            </Button>
          </Grid>
          <Grid item xs={12}>
            <Box 
              sx={{ 
                width: '100%', 
                maxWidth: '640px',
                border: '1px solid #ccc',
                borderRadius: 1,
                overflow: 'hidden',
                backgroundColor: '#000'
              }}
              id="video-feed-box"
            >
              {/* Video feed will be dynamically added here */}
            </Box>
          </Grid>
          {/* Add this debug section temporarily to verify captured images */}
          {capturedImages.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="textSecondary">
                Debug: {capturedImages.length} images captured
              </Typography>
              {capturedImages.map((img, idx) => (
                <Typography key={idx} variant="body2" color="textSecondary">
                  Image {idx + 1}: {img.dataUrl}
                </Typography>
              ))}
            </Box>
          )}
          {capturedImages.length > 0 && (
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Captured Images ({capturedImages.length})
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                You can capture multiple images. Click the X button to remove any image.
              </Typography>
              <Grid container spacing={2}>
                {capturedImages.map((image, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Paper 
                      elevation={2}
                      sx={{
                        position: 'relative',
                        padding: 1,
                        backgroundColor: '#f5f5f5',
                        minHeight: '200px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <Box
                        component="img"
                        src={image.dataUrl}
                        alt={`Captured ${index + 1}`}
                        sx={{
                          maxWidth: '100%',
                          maxHeight: '300px',
                          objectFit: 'contain'
                        }}
                      />
                      <IconButton
                        onClick={() => removeImage(index)}
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          backgroundColor: 'rgba(255, 255, 255, 0.8)',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                          }
                        }}
                      >
                        <Delete />
                      </IconButton>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          )}
          <Grid item xs={12}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={capturedImages.length === 0}
            >
              Save Images
            </Button>
          </Grid>
        </Grid>
      </form>
    </Paper>
  );
};

export default CameraCapture; 